import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';
import moment from 'moment';
import useUser from '../hooks/useUser';

export default function Users() {
    const toast = useRef(null);

    const [users, setUsers] = useState([]);
    const [visible, setVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [loading, setLoading] = useState(false);

    //Separate state for filters
    const [userFilter, setUserFilter] = useState({ client: '', branch: '' });
    const { loggedInUserId, selectedBranchId, userRole, userClient, logProcess } = useUser();

    const [formData, setFormData] = useState({
        id: null,
        username: '',
        password: '',
        rePassword: '',
        mobile: '',
        email: '',
        status: '',
        role: '',
        client: '',
        branch: '',
        platform: '',
        effectiveFrom: null,
        dataTransfer: ''
    });

    const [errors, setErrors] = useState({});

    const statusOptions = [
        { label: 'Active', value: 'Active' },
        { label: 'InActive', value: 'InActive' }
    ];

    const dataTransferOptions = [
        { label: 'Updated', value: 'updated' },
        { label: 'Pending', value: 'pending' }
    ];
    const [roles, setRoles] = useState([]);
    const [clients, setClients] = useState([]);
    const [branches, setBranches] = useState([]);
    const [platforms, setPlatforms] = useState([]);
    const formatDate = (dateString) => {
        return moment(dateString).isValid() ? moment(dateString).format('DD-MM-YYYY') : '';
    };

    //Fetch users with optional filters
    const fetchUsers = async (clientid = '', branchid = '') => {
        setLoading(true);
        try {
            if (userRole) {
                const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/users', {
                    params: { clientid, branchid, userRole, userClient }
                });
                setUsers(res.data || []);
            }
        } catch (err) {
            console.error(err);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load users' });
        } finally {
            setLoading(false); //stop loading
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchClients();
        fetchPlatforms();
        fetchRoles();
    }, [userRole]);

    const fetchClients = async () => {
        try {
            //const res = await axios.get('https://treatway.co.in/Counter-Backend/SalesApi/getClients');
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/client_creation', {
                headers: {
                    'Client-Id': userClient,
                    'User-Role': userRole
                }
            });
            setClients(res.data.map((c) => ({ label: c.client_name, value: String(c.client_id) })));
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load clients' });
        }
    };

    const fetchBranches = async (clientId) => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/branches', {
                params: { clientid: clientId }
            });
            setBranches(res.data.map((b) => ({ label: b.branch_name, value: String(b.branch_id) })));
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load branches' });
        }
    };

    const fetchPlatforms = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/platform');
            setPlatforms(
                (res.data || []).map((p) => ({
                    label: p.platform_name,
                    value: String(p.platform_id)
                }))
            );
        } catch (err) {
            console.error(err);
        }
    };

    const fetchRoles = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/roles');
            setRoles(
                (res.data || []).map((r) => ({
                    label: r.role,
                    value: String(r.roleId)
                }))
            );
        } catch (err) {
            console.error(err);
        }
    };

    // Handle input change
    const onInputChange = (e, name) => {
        setFormData({ ...formData, [name]: e.target.value });
    };

    // Validate form
    const validateForm = () => {
        let newErrors = {};
        if (!formData.username.trim()) newErrors.username = 'Username is required';
        if (!formData.password) newErrors.password = 'Password is required';
        if (!formData.rePassword) newErrors.rePassword = 'Please re-enter password';
        if (formData.password !== formData.rePassword) newErrors.rePassword = 'Passwords do not match';
        if (!formData.status) newErrors.status = 'Status is required';
        if (!formData.role) newErrors.role = 'Role is required';
        if (!formData.client) newErrors.client = 'Client is required';
        if (!formData.branch) newErrors.branch = 'Branch is required';
        if (!formData.platform) newErrors.platform = 'Platform is required';
        if (!formData.effectiveFrom) newErrors.effectiveFrom = 'Effective From date is required';
        if (!formData.dataTransfer) newErrors.dataTransfer = 'Data Transfer is required';

        if (formData.mobile && !/^\d{10}$/.test(formData.mobile)) {
            newErrors.mobile = 'Mobile number must be 10 digits';
        }

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email address';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Save user
    const saveUser = async () => {
        if (!validateForm()) {
            toast.current.show({ severity: 'error', summary: 'Validation Error', detail: 'Please fix errors' });
            return;
        }

        try {
            const payload = {
                ...formData,
                effectiveFrom: formData.effectiveFrom ? moment(formData.effectiveFrom).format('YYYY-MM-DD') : null
            };

            const url = 'https://treatway.co.in/Counter-Backend/api/server_masters/save/users';

            await axios.post(url, JSON.stringify(payload), {
                headers: { 'Content-Type': 'application/json' }
            });

            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: editingUser ? 'User Updated' : 'User Added'
            });

            setVisible(false);
            setEditingUser(null);
            setFormData({
                id: null,
                username: '',
                password: '',
                rePassword: '',
                mobile: '',
                email: '',
                status: '',
                role: '',
                client: '',
                branch: '',
                platform: '',
                effectiveFrom: null,
                dataTransfer: ''
            });
            fetchUsers(userFilter.client, userFilter.branch);
        } catch (err) {
            console.error(err);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong' });
        }
    };

    // Edit user
    const editUser = async (rowData) => {
        try {
            const res = await axios.get(`https://treatway.co.in/Counter-Backend/api/server_masters/get_user_by_id/${rowData.userId}`);
            const data = res.data;

            setEditingUser(true);
            setFormData({
                id: data.userId,
                username: data.username,
                password: '',
                rePassword: '',
                mobile: data.mobile,
                email: data.email_id,
                status: data.user_status,
                role: String(data.roleId),
                client: String(data.client_id),
                branch: String(data.branch_id),
                platform: String(data.platform_id),
                effectiveFrom: null,
                dataTransfer: data.data_transfer
            });

            await fetchBranches(data.client_id);
            setVisible(true);
        } catch (err) {
            console.error(err);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load user' });
        }
    };

    const dialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setVisible(false)} />
            <Button label="Save" icon="pi pi-check" onClick={saveUser} />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <h2 className="text-xl font-bold mb-4">Users</h2>
            <div className="card">
                <div className="formgrid grid mt-3">
                    <div className="field col-12 md:col-4">
                        <label htmlFor="client" className="font-medium">
                            Client
                        </label>
                        <Dropdown
                            value={userFilter.client}
                            options={clients}
                            onChange={(e) => {
                                const clientId = e.value || '';
                                setUserFilter({ client: clientId, branch: '' });
                                if (clientId) {
                                    fetchBranches(clientId);
                                    fetchUsers(clientId, '');
                                } else {
                                    setBranches([]);
                                    fetchUsers();
                                }
                            }}
                            placeholder="Filter by Client"
                            className="w-full"
                            filter
                        />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label htmlFor="branch" className="font-medium">
                            Branch
                        </label>
                        <Dropdown
                            value={userFilter.branch}
                            options={branches}
                            onChange={(e) => {
                                const branchId = e.value || '';
                                setUserFilter((f) => ({ ...f, branch: branchId }));
                                fetchUsers(userFilter.client, branchId);
                            }}
                            placeholder="Filter by Branch"
                            className="w-full"
                            filter
                        />
                    </div>
                    <div className="field col-12 md:col-4 flex align-items-end">
                        <Button
                            label="Reset Filters"
                            icon="pi pi-refresh"
                            className="p-button-secondary w-full"
                            onClick={() => {
                                setUserFilter({ client: '', branch: '' });
                                setBranches([]);
                                fetchUsers();
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
                    <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                        <Button
                            label="Add User"
                            icon="pi pi-plus"
                            onClick={() => {
                                setVisible(true);
                                setEditingUser(null);
                                setFormData({ id: null, username: '', password: '', rePassword: '', mobile: '', email: '', status: '', role: '', client: '', branch: '', platform: '', effectiveFrom: null, dataTransfer: '' });
                                setErrors({});
                            }}
                        />
                    </div>
                    <div className="flex items-center ml-auto min-w-[250px] justify-end w-full sm:w-auto">
                        <span className="p-input-icon-left w-full">
                            <i className="pi pi-search"></i>
                            <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." className="p-inputtext-sm w-full" />
                        </span>
                    </div>
                </div>

                <DataTable
                    value={users}
                    showGridlines
                    paginator
                    rows={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    tableStyle={{ minWidth: '50rem' }}
                    className="p-datatable-sm relative"
                    loading={loading}
                    loadingIcon={
                        //custom overlay loader
                        <div className="flex align-items-center justify-content-center w-full h-full bg-black-alpha-30 absolute top-0 left-0">
                            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" fill="var(--surface-ground)" animationDuration=".7s" />
                        </div>
                    }
                    emptyMessage="No users found."
                    globalFilter={globalFilter}
                >
                    <Column field="username" header="Username" />
                    <Column field="mobile" header="Mobile" />
                    <Column field="email_id" header="Email" />
                    <Column
                        field="user_status"
                        header="Status"
                        body={(rowData) => <span className={`px-2 py-1 text-sm font-semibold border-round text-white ${rowData.user_status === 'Active' ? 'bg-green-500' : 'bg-orange-500'}`}>{rowData.user_status}</span>}
                    />
                    <Column field="role" header="Role" />
                    <Column field="client_name" header="Client" />
                    <Column field="effective_from" header="Effective From" body={(rowData) => formatDate(rowData.effective_from)} />
                    <Column
                        field="data_transfer"
                        header="Data Transfer"
                        body={(rowData) => <span className={`px-2 py-1 text-sm font-semibold border-round text-white ${rowData.data_transfer === 'updated' ? 'bg-green-500' : 'bg-orange-500'}`}>{rowData.data_transfer}</span>}
                    />
                    <Column field="branch_name" header="Branch" />
                    <Column field="platform_name" header="Platform" />
                    <Column header="Actions" body={(rowData) => <Button icon="pi pi-pencil" className="p-button-rounded p-button-warning mr-2" onClick={() => editUser(rowData)} />} />
                </DataTable>

                {/* Dialog for Add/Edit */}
                <Dialog
                    header={editingUser ? 'Edit User' : 'Add User'}
                    visible={visible}
                    style={{ width: '700px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}
                    footer={dialogFooter}
                    onHide={() => setVisible(false)}
                    modal
                    draggable={false}
                    resizable={false}
                >
                    <div className="grid formgrid">
                        {/* Left Column */}
                        <div className="col-12 md:col-6">
                            {/* Username */}
                            <div className="field">
                                <label htmlFor="username">
                                    Username <span style={{ color: 'red' }}>*</span>
                                </label>
                                <InputText id="username" value={formData.username} onChange={(e) => onInputChange(e, 'username')} className={`w-full ${errors.username ? 'p-invalid' : ''}`} />
                                {errors.username && <small className="p-error">{errors.username}</small>}
                            </div>

                            {/* Password */}
                            <div className="field">
                                <label htmlFor="password">
                                    Password <span style={{ color: 'red' }}>*</span>
                                </label>
                                <InputText type="password" id="password" value={formData.password} onChange={(e) => onInputChange(e, 'password')} className={`w-full ${errors.password ? 'p-invalid' : ''}`} />
                                {errors.password && <small className="p-error">{errors.password}</small>}
                            </div>

                            {/* Re-enter Password */}
                            <div className="field">
                                <label htmlFor="rePassword">
                                    Re-enter Password <span style={{ color: 'red' }}>*</span>
                                </label>
                                <InputText type="password" id="rePassword" value={formData.rePassword} onChange={(e) => onInputChange(e, 'rePassword')} className={`w-full ${errors.rePassword ? 'p-invalid' : ''}`} />
                                {errors.rePassword && <small className="p-error">{errors.rePassword}</small>}
                            </div>

                            {/* Mobile */}
                            <div className="field">
                                <label htmlFor="mobile">Mobile</label>
                                <InputText id="mobile" value={formData.mobile} onChange={(e) => onInputChange(e, 'mobile')} className="w-full" />
                                {errors.mobile && <small className="p-error">{errors.mobile}</small>}
                            </div>

                            {/* Email */}
                            <div className="field">
                                <label htmlFor="email">Email</label>
                                <InputText id="email" value={formData.email} onChange={(e) => onInputChange(e, 'email')} className="w-full" />
                                {errors.email && <small className="p-error">{errors.email}</small>}
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="col-12 md:col-6">
                            {/* Client */}
                            <div className="field">
                                <label htmlFor="client">Client *</label>
                                {editingUser ? (
                                    <InputText value={clients.find((c) => c.value === formData.client)?.label || ''} disabled className="w-full" />
                                ) : (
                                    <Dropdown
                                        id="client"
                                        value={formData.client}
                                        options={clients}
                                        onChange={(e) => {
                                            const clientId = e.value;
                                            setFormData({ ...formData, client: clientId, branch: '' });
                                            fetchBranches(clientId);
                                        }}
                                        className={`w-full ${errors.client ? 'p-invalid' : ''}`}
                                        placeholder="Select Client"
                                    />
                                )}
                                {errors.client && <small className="p-error">{errors.client}</small>}
                            </div>

                            {/* Branch */}
                            <div className="field">
                                <label htmlFor="branch">Branch *</label>
                                {editingUser ? (
                                    <InputText value={branches.find((b) => b.value === formData.branch)?.label || ''} disabled className="w-full" />
                                ) : (
                                    <Dropdown
                                        id="branch"
                                        value={formData.branch}
                                        options={branches}
                                        onChange={(e) => setFormData({ ...formData, branch: e.value })}
                                        className={`w-full ${errors.branch ? 'p-invalid' : ''}`}
                                        placeholder="Select Branch"
                                    />
                                )}
                                {errors.branch && <small className="p-error">{errors.branch}</small>}
                            </div>

                            {/* Role */}
                            <div className="field">
                                <label htmlFor="role">
                                    Role <span style={{ color: 'red' }}>*</span>
                                </label>
                                <Dropdown id="role" value={formData.role} options={roles} onChange={(e) => setFormData({ ...formData, role: e.value })} className={`w-full ${errors.role ? 'p-invalid' : ''}`} placeholder="Select Role" />
                                {errors.role && <small className="p-error">{errors.role}</small>}
                            </div>

                            {/* Platform */}
                            <div className="field">
                                <label htmlFor="platform">
                                    Platform <span style={{ color: 'red' }}>*</span>
                                </label>
                                <Dropdown
                                    id="platform"
                                    value={formData.platform}
                                    options={platforms}
                                    onChange={(e) => setFormData({ ...formData, platform: e.value })}
                                    className={`w-full ${errors.platform ? 'p-invalid' : ''}`}
                                    placeholder="Select Platform"
                                />
                                {errors.platform && <small className="p-error">{errors.platform}</small>}
                            </div>

                            {/* Status */}
                            <div className="field">
                                <label htmlFor="status">
                                    Status <span style={{ color: 'red' }}>*</span>
                                </label>
                                <Dropdown
                                    id="status"
                                    value={formData.status}
                                    options={statusOptions}
                                    onChange={(e) => setFormData({ ...formData, status: e.value })}
                                    className={`w-full ${errors.status ? 'p-invalid' : ''}`}
                                    placeholder="Select Status"
                                />
                                {errors.status && <small className="p-error">{errors.status}</small>}
                            </div>
                        </div>

                        {/* Effective From + Data Transfer — single row */}
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label>
                                    Effective From <span style={{ color: 'red' }}>*</span>
                                </label>
                                <Calendar value={formData.effectiveFrom} onChange={(e) => setFormData({ ...formData, effectiveFrom: e.value })} dateFormat="dd-mm-yy" className={`w-full ${errors.effectiveFrom ? 'p-invalid' : ''}`} />
                                {errors.effectiveFrom && <small className="p-error">{errors.effectiveFrom}</small>}
                            </div>
                        </div>
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label>
                                    Data Transfer <span style={{ color: 'red' }}>*</span>
                                </label>
                                <Dropdown value={formData.dataTransfer} options={dataTransferOptions} onChange={(e) => onInputChange(e, 'dataTransfer')} className={`w-full ${errors.dataTransfer ? 'p-invalid' : ''}`} />
                                {errors.dataTransfer && <small className="p-error">{errors.dataTransfer}</small>}
                            </div>
                        </div>
                    </div>
                </Dialog>
            </div>
        </>
    );
}
